import React, {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import styled from "@emotion/styled";
import {pluralize} from "@rw/string_utils";
import {carsTheme} from "@rw/themes";
import {ErrorMessage, Form, Formik, FormikHelpers} from "formik";
import {debounce, omitBy} from "lodash";

import {IStore} from "../app/reducers/hybrid_reducer";
import {apiLink} from "../app/routes/api_link";
import {appendQueryString} from "../app/utils/append_query_string";
import {getRequest} from "../app/utils/request_response_utils/request";
import {carBodyTypes, engineTypes} from "../dashboard/add_car/options/select_options";
import {bgc_primary, bgc_primary_hover} from "../styles/helpers";
import {ClearableSelect} from "./ClearableSelect";
import {locationParamFromValues} from "./location_filter_utils";
import {SearchLocationFields} from "./SearchLocationFields";
import {
    GEARBOX_FILTER_OPTIONS,
    MILEAGE_FILTER_OPTIONS,
    PRICE_FILTER_OPTIONS,
    SearchFormValues,
    SORT_FILTER_OPTIONS,
    YEAR_FILTER_OPTIONS,
    buildOfferListUrlFromSearchValues,
    emptySearchFormValues,
    searchFormValidationSchema
} from "./search_filter_config";

interface ISearchFacetState {
    count: number;
    model: string[];
}

interface IAllSearchDataJsonResponse {
    count: number;
    model: string[];
}

export type CarSearchFiltersVariant = "homepage" | "mobile" | "list";

interface ICarSearchFiltersFormProps {
    variant: CarSearchFiltersVariant;
    initialValues?: Partial<SearchFormValues>;
    /** Human-readable SEO location shown on list filters hint. */
    locationLabel?: string;
    /** Polish city label for the city input (SSR seed). */
    initialCityLabel?: string;
    hideMake?: boolean;
    /** Seed offer count (e.g. current list totalItems from SSR). */
    initialCount?: number;
}

const locationHrefFromSearchValues = (values: SearchFormValues): string => {
    const url = decodeURIComponent(buildOfferListUrlFromSearchValues(values));
    try {
        const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost");
        return `${parsed.pathname}${parsed.search}`;
    } catch {
        return url;
    }
};

const currentLocationKey = (): string => {
    if (typeof window === "undefined") {
        return "";
    }
    return `${window.location.pathname}${window.location.search}`;
};

const CarSearchFiltersForm: React.FC<ICarSearchFiltersFormProps> = ({
    variant,
    initialValues: initialValuesProp,
    locationLabel,
    initialCityLabel,
    hideMake = false,
    initialCount
}) => {
    const carsAllMakes = useSelector((store: IStore) => store.carList.cars.allMakes);
    const initialSearchData = useSelector((store: IStore) => store.carList.cars.initialSearchData);
    const skipInitialFacetFetch = useRef(variant !== "list" && typeof initialSearchData?.count === "number");
    const skipInitialListNavigate = useRef(variant === "list");

    const mergedInitial: SearchFormValues = {
        ...emptySearchFormValues,
        ...initialValuesProp
    };

    const [facetState, setFacetState] = useState<ISearchFacetState>({
        count: initialCount ?? initialSearchData?.count ?? 0,
        model: mergedInitial.make ? initialSearchData?.model || [] : []
    });
    const [isFacetLoading, setIsFacetLoading] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);

    const isMobile = variant === "mobile";
    const isList = variant === "list";
    /** Homepage / mobile: short form like Otomoto; full filters live on /osobowe/. */
    const isCompact = !isList;
    const pluralOffer = pluralize(["oferta", "oferty", "ofert"]);

    // List count comes from SSR / list response — keep in sync, don't overwrite with global search.
    useEffect(() => {
        if (isList && typeof initialCount === "number") {
            setFacetState((prev) => (prev.count === initialCount ? prev : {...prev, count: initialCount}));
        }
    }, [isList, initialCount]);

    const navigateToFilters = useCallback((values: SearchFormValues) => {
        const next = locationHrefFromSearchValues(values);
        if (next === currentLocationKey()) {
            return;
        }
        setIsNavigating(true);
        window.location.href = next;
    }, []);

    const debouncedNavigate = useMemo(
        () =>
            debounce((values: SearchFormValues) => {
                navigateToFilters(values);
            }, 400),
        [navigateToFilters]
    );

    const buildFacetParams = useCallback((values: SearchFormValues) => {
        const cleanedValues = omitBy(
            {
                make: values.make,
                model: values.model,
                typ: values.typ,
                fuel: values.fuel,
                yearFrom: values.yearFrom,
                yearTo: values.yearTo,
                price__gte: values.price__gte,
                price__lte: values.price__lte,
                millage__gte: values.millage__gte,
                millage__lte: values.millage__lte,
                gearbox: values.gearbox
            },
            (value) => value === ""
        ) as Record<string, string>;
        const locationParam = locationParamFromValues(values.location, values.subLocation);
        if (locationParam) {
            cleanedValues.location = locationParam;
        }
        return cleanedValues;
    }, []);

    const debouncedFacetFetch = useMemo(
        () =>
            debounce((values: SearchFormValues) => {
                setIsFacetLoading(true);

                const url = appendQueryString(apiLink.carDynamicSearch({})({}), buildFacetParams(values));
                getRequest({}, url, "fetchDynamicSearch")
                    .then(async (json: IAllSearchDataJsonResponse) => {
                        setFacetState({
                            count: json.count,
                            model: values.make ? json.model || [] : []
                        });
                    })
                    .finally(() => {
                        setIsFacetLoading(false);
                    });
            }, 400),
        [buildFacetParams]
    );

    /** List mount: load models for current make without spinner / without clobbering SSR count. */
    const prefetchListModels = useCallback((values: SearchFormValues) => {
        if (!values.make) {
            return;
        }
        const url = appendQueryString(apiLink.carDynamicSearch({})({}), buildFacetParams(values));
        getRequest({}, url, "fetchDynamicSearchModels").then(async (json: IAllSearchDataJsonResponse) => {
            setFacetState((prev) => ({
                ...prev,
                model: json.model || []
            }));
        });
    }, [buildFacetParams]);

    useEffect(() => {
        return () => {
            debouncedNavigate.cancel();
            debouncedFacetFetch.cancel();
        };
    }, [debouncedNavigate, debouncedFacetFetch]);

    const handleSubmit = async (values: SearchFormValues, _helpers: FormikHelpers<SearchFormValues>) => {
        navigateToFilters(values);
    };

    const handleMakeChange = (
        event: ChangeEvent<HTMLSelectElement>,
        setValues: (values: SearchFormValues) => void,
        values: SearchFormValues
    ) => {
        setValues({
            ...values,
            make: event.target.value,
            model: ""
        });
        if (!event.target.value) {
            setFacetState((prev) => ({...prev, model: []}));
        }
    };

    const onValuesChange = useCallback(
        (values: SearchFormValues) => {
            if (isList) {
                // Instant-apply list: count is SSR totalItems. Don't re-query global facets on mount.
                if (skipInitialListNavigate.current) {
                    skipInitialListNavigate.current = false;
                    prefetchListModels(values);
                    return;
                }
                debouncedNavigate(values);
                return;
            }

            if (skipInitialFacetFetch.current) {
                skipInitialFacetFetch.current = false;
                return;
            }
            debouncedFacetFetch(values);
        },
        [isList, debouncedNavigate, debouncedFacetFetch, prefetchListModels]
    );

    const submitLabel = (() => {
        if (isFacetLoading) {
            return null;
        }
        if (facetState.count === 0) {
            return "Brak ofert";
        }
        return `Pokaż ${facetState.count} ${pluralOffer(facetState.count)}`;
    })();

    return (
        <Formik<SearchFormValues>
            initialValues={mergedInitial}
            enableReinitialize={isList}
            validationSchema={searchFormValidationSchema}
            onSubmit={handleSubmit}
        >
            {({values, handleChange, setValues}) => (
                <FiltersFormBody
                    variant={variant}
                    values={values}
                    handleChange={handleChange}
                    setValues={setValues}
                    handleMakeChange={handleMakeChange}
                    onValuesChange={onValuesChange}
                    onReset={() => {
                        setValues({...emptySearchFormValues});
                        setFacetState((prev) => ({...prev, model: []}));
                    }}
                    carsAllMakes={carsAllMakes}
                    facetState={facetState}
                    isFacetLoading={isFacetLoading}
                    isNavigating={isNavigating}
                    hideMake={hideMake}
                    isMobile={isMobile}
                    isList={isList}
                    isCompact={isCompact}
                    locationLabel={locationLabel}
                    initialCityLabel={initialCityLabel}
                    submitLabel={submitLabel}
                    pluralOffer={pluralOffer}
                />
            )}
        </Formik>
    );
};

const hasActiveFilters = (values: SearchFormValues): boolean =>
    Object.values(values).some((value) => value !== "");

interface IFiltersFormBodyProps {
    variant: CarSearchFiltersVariant;
    values: SearchFormValues;
    handleChange: (e: React.ChangeEvent<any>) => void;
    setValues: (values: SearchFormValues) => void;
    handleMakeChange: (
        event: ChangeEvent<HTMLSelectElement>,
        setValues: (values: SearchFormValues) => void,
        values: SearchFormValues
    ) => void;
    onValuesChange: (values: SearchFormValues) => void;
    onReset: () => void;
    carsAllMakes: Array<{make: string; count: number}>;
    facetState: ISearchFacetState;
    isFacetLoading: boolean;
    isNavigating: boolean;
    hideMake: boolean;
    isMobile: boolean;
    isList: boolean;
    isCompact: boolean;
    locationLabel?: string;
    initialCityLabel?: string;
    submitLabel: string | null;
    pluralOffer: (n: number) => string;
}

const FiltersFormBody: React.FC<IFiltersFormBodyProps> = ({
    variant,
    values,
    handleChange,
    setValues,
    handleMakeChange,
    onValuesChange,
    onReset,
    carsAllMakes,
    facetState,
    isFacetLoading,
    isNavigating,
    hideMake,
    isMobile,
    isList,
    isCompact,
    locationLabel,
    initialCityLabel,
    submitLabel,
    pluralOffer
}) => {
    const onValuesChangeRef = useRef(onValuesChange);
    onValuesChangeRef.current = onValuesChange;
    const filtersActive = hasActiveFilters(values);
    const makeOptions = carsAllMakes.map((make) => {
        const makeLabel = make.make === "mercedesbenz" ? "Mercedes-Benz" : make.make;
        return {
            value: make.make.toLowerCase(),
            label: `${makeLabel} (${make.count})`
        };
    });
    const modelOptions = facetState.model.map((model) => ({value: model, label: model}));
    const yearOptions = YEAR_FILTER_OPTIONS.map((year) => ({value: year, label: year}));

    useEffect(() => {
        onValuesChangeRef.current(values);
    }, [values]);

    return (
        <FormWrapper data-variant={variant}>
            {isList ? (
                <ListFiltersHeader>
                    <ListFiltersHeaderText>
                        <ListFiltersTitle>Filtruj wyniki</ListFiltersTitle>
                        <ListFiltersHint>
                            {isNavigating ? (
                                <>
                                    <HintSpinner aria-hidden /> Ładuję oferty…
                                </>
                            ) : (
                                <>
                                    {facetState.count} {pluralOffer(facetState.count)}
                                    {locationLabel ? ` · ${locationLabel}` : null}
                                </>
                            )}
                        </ListFiltersHint>
                    </ListFiltersHeaderText>
                    <ResetButton type="button" onClick={onReset} disabled={!filtersActive || isNavigating}>
                        Wyczyść
                    </ResetButton>
                </ListFiltersHeader>
            ) : null}
            <Form>
                <FormGrid data-variant={variant}>
                    {!hideMake ? (
                        <FormControl>
                            <Label htmlFor={`make-${variant}`}>Marka</Label>
                            <ClearableSelect
                                id={`make-${variant}`}
                                name="make"
                                value={values.make}
                                options={makeOptions}
                                clearLabel="Wyczyść markę"
                                onChange={(event) => handleMakeChange(event, setValues, values)}
                                onClear={() => setValues({...values, make: "", model: ""})}
                            />
                            <ErrorMessage name="make" component={ErrorMessageWrapper} />
                        </FormControl>
                    ) : null}

                    <FormControl>
                        <Label htmlFor={`model-${variant}`}>Model</Label>
                        <ClearableSelect
                            id={`model-${variant}`}
                            name="model"
                            value={values.model}
                            options={modelOptions}
                            placeholder={values.make ? "Wybierz" : "Najpierw wybierz markę"}
                            disabled={!values.make}
                            clearLabel="Wyczyść model"
                            onChange={handleChange}
                            onClear={() => setValues({...values, model: ""})}
                        />
                    </FormControl>

                    <SearchLocationFields
                        variant={variant}
                        values={values}
                        setValues={setValues}
                        initialCityLabel={initialCityLabel}
                    />

                    <FormControl>
                        <Label htmlFor={`price_lte-${variant}`}>Cena do</Label>
                        <ClearableSelect
                            id={`price_lte-${variant}`}
                            name="price__lte"
                            value={values.price__lte}
                            options={PRICE_FILTER_OPTIONS}
                            clearLabel="Wyczyść cenę do"
                            onChange={handleChange}
                            onClear={() => setValues({...values, price__lte: ""})}
                        />
                    </FormControl>

                    <FormControl>
                        <Label htmlFor={`yearFrom-${variant}`}>Rocznik od</Label>
                        <ClearableSelect
                            id={`yearFrom-${variant}`}
                            name="yearFrom"
                            value={values.yearFrom}
                            options={yearOptions}
                            clearLabel="Wyczyść rocznik od"
                            onChange={handleChange}
                            onClear={() => setValues({...values, yearFrom: ""})}
                        />
                    </FormControl>

                    <FormControl>
                        <Label htmlFor={`fuel-${variant}`}>Paliwo</Label>
                        <ClearableSelect
                            id={`fuel-${variant}`}
                            name="fuel"
                            value={values.fuel}
                            options={engineTypes}
                            clearLabel="Wyczyść paliwo"
                            onChange={handleChange}
                            onClear={() => setValues({...values, fuel: ""})}
                        />
                    </FormControl>

                    {!isCompact ? (
                        <>
                            <FormControl>
                                <Label htmlFor={`typ-${variant}`}>Typ</Label>
                                <ClearableSelect
                                    id={`typ-${variant}`}
                                    name="typ"
                                    value={values.typ}
                                    options={carBodyTypes}
                                    clearLabel="Wyczyść typ"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, typ: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`yearTo-${variant}`}>Rocznik do</Label>
                                <ClearableSelect
                                    id={`yearTo-${variant}`}
                                    name="yearTo"
                                    value={values.yearTo}
                                    options={yearOptions}
                                    clearLabel="Wyczyść rocznik do"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, yearTo: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`price_gte-${variant}`}>Cena od</Label>
                                <ClearableSelect
                                    id={`price_gte-${variant}`}
                                    name="price__gte"
                                    value={values.price__gte}
                                    options={PRICE_FILTER_OPTIONS}
                                    clearLabel="Wyczyść cenę od"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, price__gte: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`millage_gte-${variant}`}>Przebieg od</Label>
                                <ClearableSelect
                                    id={`millage_gte-${variant}`}
                                    name="millage__gte"
                                    value={values.millage__gte}
                                    options={MILEAGE_FILTER_OPTIONS}
                                    clearLabel="Wyczyść przebieg od"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, millage__gte: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`millage_lte-${variant}`}>Przebieg do</Label>
                                <ClearableSelect
                                    id={`millage_lte-${variant}`}
                                    name="millage__lte"
                                    value={values.millage__lte}
                                    options={MILEAGE_FILTER_OPTIONS}
                                    clearLabel="Wyczyść przebieg do"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, millage__lte: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`gearbox-${variant}`}>Skrzynia</Label>
                                <ClearableSelect
                                    id={`gearbox-${variant}`}
                                    name="gearbox"
                                    value={values.gearbox}
                                    options={GEARBOX_FILTER_OPTIONS}
                                    clearLabel="Wyczyść skrzynię"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, gearbox: ""})}
                                />
                            </FormControl>

                            <FormControl>
                                <Label htmlFor={`sort-${variant}`}>Sortowanie</Label>
                                <ClearableSelect
                                    id={`sort-${variant}`}
                                    name="sort"
                                    value={values.sort}
                                    options={SORT_FILTER_OPTIONS}
                                    placeholder="Domyślne"
                                    clearLabel="Wyczyść sortowanie"
                                    onChange={handleChange}
                                    onClear={() => setValues({...values, sort: ""})}
                                />
                            </FormControl>
                        </>
                    ) : null}

                    {!isList ? (
                        <ActionsCell data-variant={variant} data-compact={isCompact ? "1" : undefined}>
                            <ResetButton type="button" onClick={onReset} disabled={!filtersActive || isFacetLoading}>
                                Wyczyść
                            </ResetButton>
                            {isCompact ? (
                                <MoreFiltersButton
                                    type="button"
                                    onClick={() => {
                                        window.location.href = decodeURIComponent(buildOfferListUrlFromSearchValues(values));
                                    }}
                                >
                                    Więcej filtrów
                                </MoreFiltersButton>
                            ) : null}
                            <SubmitButton type="submit" data-variant={variant} data-loading={isFacetLoading ? "1" : undefined}>
                                {isFacetLoading ? (
                                    <>
                                        <ButtonSpinner aria-hidden />
                                        Wyszukuję oferty…
                                    </>
                                ) : (
                                    <>
                                        {submitLabel}
                                        {facetState.count === 0 ? <small>Zmień filtry</small> : null}
                                    </>
                                )}
                            </SubmitButton>
                        </ActionsCell>
                    ) : null}
                </FormGrid>
            </Form>
        </FormWrapper>
    );
};

export default CarSearchFiltersForm;

const FormWrapper = styled.div`
    &[data-variant="homepage"] {
        display: none;
        @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_md}) {
            width: 100%;
            max-width: 1200px;
            margin: -40px auto 0 auto;
            padding: 40px;
            border: 1px solid #ccc;
            background: #fff;
            z-index: 10;
            position: relative;
            box-shadow: 0 6px 16px #e5e5e5;
            border-radius: 8px;
            display: block;
        }
    }

    &[data-variant="list"] {
        width: 100%;
        margin: 0 0 24px 0;
        padding: 20px;
        border: 1px solid #e5e5e5;
        background: #fff;
        border-radius: 8px;
    }

    &[data-variant="mobile"] {
        width: 100%;
        max-width: 1200px;
        margin: -40px auto 0 auto;
        padding: 40px;
        border: 1px solid #ccc;
        background: #fff;
        z-index: 10;
        position: relative;
        box-shadow: 0 6px 16px #e5e5e5;
        border-radius: 8px;
    }
`;

const ListFiltersHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
`;

const ListFiltersHeaderText = styled.div`
    min-width: 0;
    flex: 1;
`;

const ListFiltersTitle = styled.p`
    margin: 0 0 4px;
    font-size: 1.1rem;
    font-weight: 700;
    color: ${carsTheme.colors.brand_black};
`;

const ListFiltersHint = styled.p`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    min-height: 24px;
    font-size: 14px;
    color: ${carsTheme.colors.gray};
`;

const HintSpinner = styled.span`
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 0, 0, 0.12);
    border-top-color: ${carsTheme.colors.gray};
    border-radius: 50%;
    flex-shrink: 0;
    animation: carsFilterHintSpin 0.7s linear infinite;

    @keyframes carsFilterHintSpin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const FormGrid = styled.div`
    display: grid;
    gap: 16px 16px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: end;

    &[data-variant="list"] {
        grid-template-columns: repeat(4, minmax(0, 1fr));

        @media screen and (max-width: ${(props) => props.theme.breakpoints.screen_lg}) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        @media screen and (max-width: ${(props) => props.theme.breakpoints.screen_md}) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @media screen and (max-width: ${(props) => props.theme.breakpoints.screen_sm}) {
            grid-template-columns: minmax(0, 1fr);
        }
    }

    &[data-variant="homepage"] {
        @media screen and (max-width: ${(props) => props.theme.breakpoints.screen_lg}) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    &[data-variant="mobile"] {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const FormControl = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 0;
`;

const Label = styled.label`
    margin-bottom: 5px;
    color: ${carsTheme.colors.gray};
    font-size: 14px;
`;

const ErrorMessageWrapper = styled.div`
    color: red;
    font-size: 12px;
    margin-top: 5px;
    position: absolute;
    bottom: -20px;
`;

const ActionsCell = styled.div`
    display: flex;
    align-items: end;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;

    &[data-variant="homepage"] {
        grid-column: span 1;
    }

    &[data-variant="homepage"][data-compact="1"] {
        grid-column: 1 / -1;
        justify-content: stretch;

        > button {
            flex: 1 1 0;
        }
    }

    &[data-variant="mobile"] {
        grid-column: 1 / -1;

        > button {
            flex: 1 1 auto;
        }
    }
`;

const MoreFiltersButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    padding: 10px 16px;
    min-width: 140px;
    height: 48px;
    font-size: 14px;
    font-weight: 600;
    color: ${carsTheme.colors.brand_black};
    background: #fff;
    border: 2px solid ${carsTheme.colors.gray_light};
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        border-color: ${carsTheme.colors.brand_primary};
        color: ${carsTheme.colors.brand_primary};
    }
`;

const ResetButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    padding: 10px 16px;
    min-width: 96px;
    height: 42px;
    font-size: 14px;
    font-weight: 600;
    color: ${carsTheme.colors.brand_black};
    background: #fff;
    border: 2px solid ${carsTheme.colors.gray_light};
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
        border-color: ${carsTheme.colors.brand_primary};
        color: ${carsTheme.colors.brand_primary};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ButtonSpinner = styled.span`
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    flex-shrink: 0;
    animation: carsFilterBtnSpin 0.7s linear infinite;

    @keyframes carsFilterBtnSpin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    flex: 0 0 auto;
    padding: 10px 20px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    ${bgc_primary};
    border: 2px solid ${carsTheme.colors.brand_primary};
    border-radius: 4px;
    cursor: pointer;
    min-width: 160px;
    width: auto;
    height: 48px;
    line-height: 1.2;

    &[data-loading="1"] {
        flex-direction: row;
        gap: 8px;
        opacity: 0.92;
    }

    &[data-variant="mobile"] {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
    }

    &:hover {
        ${bgc_primary_hover};
        color: ${carsTheme.colors.brand_black};
        background: #fff;
    }

    &[data-loading="1"]:hover {
        ${bgc_primary};
        color: #fff;
        background: ${carsTheme.colors.brand_primary};
    }

    small {
        font-size: 12px;
        font-weight: 400;
        opacity: 0.9;
    }
`;
